import React, { createContext, ReactElement, useReducer } from 'react';

export interface Default {
  push: (screen: string | PushPayload) => void;
  previousView: string;
  goBack: () => void;
}

export const RouterContext = createContext<Default>({} as Default);

interface State {
  previousView: string;
  currentView: string;
  params: any;
}

type PushPayload = { screen: string; params: any };

type Action = { type: 'PUSH'; payload: PushPayload } | { type: 'GO_BACK' };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'PUSH':
      return {
        ...state,
        previousView: state.currentView,
        currentView: action.payload.screen,
        params: action.payload.params,
      };
    case 'GO_BACK':
      return { ...state, previousView: state.currentView, currentView: state.previousView };
    default:
      throw new Error('Action not defined');
  }
}

interface Props {
  children: any;
  entry: string;
}

export const RouterProvider: React.FC<Props> = ({ children, entry }) => {
  const [{ currentView, previousView, params }, dispatch] = useReducer(reducer, {
    previousView: '',
    currentView: entry,
    params: {},
  });

  const push = async (screen: string | PushPayload): Promise<void> => {
    if (typeof screen === 'string') {
      dispatch({
        type: 'PUSH',
        payload: {
          screen,
          params: {},
        },
      });
    } else {
      dispatch({ type: 'PUSH', payload: screen });
    }
  };

  const goBack = (): void => dispatch({ type: 'GO_BACK' });

  const childArray = React.Children.toArray(children);
  const matchedChild = childArray.find(child => (child as ReactElement).props.screen === currentView);

  return (
    <RouterContext.Provider
      value={{
        push,
        goBack,
        previousView,
      }}
    >
      {React.cloneElement(matchedChild as ReactElement, { params })}
    </RouterContext.Provider>
  );
};
